/**
 * Locale-aware, user-facing formatting helpers.
 *
 * Mail dates carry a declared zone offset; we display the moment in the
 * user's zone (what "when did this arrive" means), and keep the declared
 * offset available for the header view.
 */
import type { Address } from "../engine/types.ts";

/**
 * Formatters are cached per (purpose, locale, zone): construction is the
 * expensive part.
 */
const formatterCache = new Map<string, Intl.DateTimeFormat>();

/**
Format recipes, by purpose key.
 */
const RECIPES: Readonly<Record<string, Intl.DateTimeFormatOptions>> = {
  time: { hour: "2-digit", minute: "2-digit" },
  day: { day: "numeric", month: "short" },
  full: { day: "numeric", month: "short", year: "numeric" },
  medium: { dateStyle: "medium", timeStyle: "short" },
};

/**
Knobs shared by the date formatters.
 */
export interface DateOptions {
  /**
  BCP 47 locale tag; defaults to the runtime locale.
   */
  readonly locale?: string;
  /**
  IANA zone name; defaults to the runtime zone. Tests pass `'UTC'`.
   */
  readonly timeZone?: string;
}

/**
Options of {@link formatListDate}.
 */
export interface ListDateOptions extends DateOptions {
  /**
  Reference "now" in unix seconds; injectable so tests are deterministic.
   */
  readonly now?: number;
}

/**
 * Formats a timestamp as a concise list-row date.
 *
 * Today renders as time only, this year as "7 Apr", otherwise a full date.
 *
 * @param timestamp - Unix seconds.
 * @param options - Locale, zone, and reference time.
 * @returns The formatted label.
 */
export function formatListDate(
  timestamp: number,
  options: ListDateOptions = {},
): string {
  const { locale, timeZone, now = Math.floor(Date.now() / 1000) } = options;
  const date = new Date(timestamp * 1000);
  const reference = new Date(now * 1000);
  // Comparison via toDateString stays in one (the runtime) zone for both
  // sides, so "today" is judged in the user's day, whatever it is.
  const isSameDay = date.toDateString() === reference.toDateString();
  if (isSameDay) {
    return cachedFormatter("time", locale, timeZone).format(date);
  }
  if (date.getFullYear() === reference.getFullYear()) {
    return cachedFormatter("day", locale, timeZone).format(date);
  }
  return cachedFormatter("full", locale, timeZone).format(date);
}

/**
 * Formats a timestamp for the reader header, including the source offset.
 *
 * @param timestamp - Unix seconds.
 * @param offsetSeconds - Zone offset declared by the message, if any.
 * @param options - Locale and zone.
 * @returns e.g. "15 Apr 2025, 10:15 (UTC+00:00)".
 */
export function formatFullDate(
  timestamp: number,
  offsetSeconds: number | null,
  options: DateOptions = {},
): string {
  const { locale, timeZone } = options;
  const date = new Date(timestamp * 1000);
  const base = cachedFormatter("medium", locale, timeZone).format(date);
  if (offsetSeconds === null) {
    return base;
  }
  return `${base} (${formatOffset(offsetSeconds)})`;
}

/**
 * Human-readable byte size: `1.2 MB`, `340 kB`, `8 B` (SI units, one decimal
 * below 100).
 *
 * @param bytes - Size in bytes.
 * @returns The formatted label.
 */
export function formatSize(bytes: number): string {
  if (bytes < 1000) {
    return `${bytes} B`;
  }
  const units = ["kB", "MB", "GB"];
  let value = bytes;
  let unit = -1;
  do {
    value /= 1000;
    unit += 1;
  } while (value >= 1000 && unit < units.length - 1);
  return `${value >= 100 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
}

/**
 * Formats a parsed address list for display.
 *
 * @param addresses - Parsed `From`/`To`/`Cc` mailboxes.
 * @returns `"Ann North <ann@example.com>", …` joined for text contexts.
 */
export function formatAddresses(addresses: readonly Address[]): string {
  return addresses
    .map(({ display_name, address }) =>
      display_name === null ? address : `${display_name} <${address}>`,
    )
    .join(", ");
}

/**
 * Renders "UTC+05:30"-style offsets from seconds.
 *
 * @param offsetSeconds - Zone offset in seconds (negative west of UTC).
 * @returns The formatted offset.
 */
function formatOffset(offsetSeconds: number): string {
  const sign = offsetSeconds < 0 ? "-" : "+";
  const absolute = Math.abs(offsetSeconds);
  const hours = Math.floor(absolute / 3600);
  const minutes = Math.floor((absolute % 3600) / 60);
  return `UTC${sign}${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

/**
 * Returns a shared formatter for one (purpose, locale, zone) triple.
 *
 * @param purpose - Short key identifying the format recipe.
 * @param locale - BCP 47 tag or `undefined` for the runtime locale.
 * @param timeZone - IANA zone or `undefined` for the runtime zone.
 * @returns The cached formatter.
 */
function cachedFormatter(
  purpose: string,
  locale: string | undefined,
  timeZone: string | undefined,
): Intl.DateTimeFormat {
  const key = `${purpose} ${locale ?? ""} ${timeZone ?? ""}`;
  const existing = formatterCache.get(key);
  if (existing) {
    return existing;
  }
  const options = RECIPES[purpose];
  if (options === undefined) {
    throw new Error(`Unknown formatter recipe: ${purpose}`);
  }
  const created = new Intl.DateTimeFormat(
    locale,
    timeZone === undefined ? options : { ...options, timeZone },
  );
  formatterCache.set(key, created);
  return created;
}
